import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, CreditCard, Info, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useTransactionFeesSettings } from '~entities/payment/api/fetch-transaction-fees-settings';
import { useGetAllowedPaymentMethodsConfig } from '~entities/payment/api/get-allowed-payment-methods';
import { useToggleTransactionFees } from '~entities/payment/api/toggle-transaction-fee-settings';
import {
  DEFAULT_TRANSACTION_FEES,
  PASS_ALONG_LINK_DISABLED_NOTICE,
  PASS_ALONG_LINK_NOT_CHARGED_NOTICE,
  PASS_TRANSACTION_FEES_DISCLOSURE,
} from '~entities/payment/constants/transaction-fee';
import { useGetStudioDefaultPaymentProvider } from '~entities/studios/api/get-studio-default-payment-provider';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { ExcludedParentsTable } from '~features/payment/ui/excluded-parents-table';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { Separator } from '~shared/shadcn/ui/separator';
import { Switch } from '~shared/shadcn/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~shared/shadcn/ui/tooltip';

export const TransactionFeeSettings = () => {
  // API hooks
  const { data: settings, isFetching: isFetchingSettings } =
    useTransactionFeesSettings();
  const { mutateAsync: toggleTransactionFees, isPending: isUpdating } =
    useToggleTransactionFees();
  const { data: currencyData, isFetching: isFetchingCurrency } =
    useGetCurrency();
  const { data: studioDetails } = useGetStudioDetails();
  const { data: studioPaymentProvider } = useGetStudioDefaultPaymentProvider();
  const locationId = studioDetails?.locationId ?? '';
  const { data: paymentMethodsConfig } =
    useGetAllowedPaymentMethodsConfig(locationId);
  const bypassStripePaymentMethodConfig =
    paymentMethodsConfig?.bypassStripePaymentMethodConfig === true;
  const isAUD = currencyData?.name === 'AUD';
  const isUSD = currencyData?.name === 'USD';

  const activeProvider = studioPaymentProvider?.paymentProvider
    ? `${studioPaymentProvider.paymentProvider.charAt(0).toUpperCase()}${studioPaymentProvider.paymentProvider.slice(1)}`
    : null;

  // Form state
  const [passFeesToParent, setPassFeesToParent] = useState(false);
  const [cardFeePercentage, setCardFeePercentage] = useState<string>(
    DEFAULT_TRANSACTION_FEES.CARD_TRANSACTION_PERCENTAGE,
  );
  const [cardFixedFee, setCardFixedFee] = useState<string>(
    DEFAULT_TRANSACTION_FEES.CARD_FIXED,
  );
  const [achFeePercentage, setAchFeePercentage] = useState<string>(
    isUSD || isAUD
      ? DEFAULT_TRANSACTION_FEES.ACH_TRANSACTION_PERCENTAGE
      : DEFAULT_TRANSACTION_FEES.NO_FEE,
  );
  const [showEnableFeesConfirmation, setShowEnableFeesConfirmation] =
    useState(false);

  // Update form state when API data loads
  useEffect(() => {
    if (settings) {
      setPassFeesToParent(settings.passTransactionFeesToCustomers);
      setCardFeePercentage(
        settings.cardTransactionFees ??
          (isAUD
            ? DEFAULT_TRANSACTION_FEES.AUD_CARD_TRANSACTION_PERCENTAGE
            : DEFAULT_TRANSACTION_FEES.CARD_TRANSACTION_PERCENTAGE),
      );
      setCardFixedFee(
        settings.cardFixedFee ?? DEFAULT_TRANSACTION_FEES.CARD_FIXED,
      );
      setAchFeePercentage(
        settings.achTransactionFees ??
          (isUSD || isAUD
            ? DEFAULT_TRANSACTION_FEES.ACH_TRANSACTION_PERCENTAGE
            : DEFAULT_TRANSACTION_FEES.NO_FEE),
      );
    }
  }, [settings, isAUD, isUSD]);

  const handleSave = async () => {
    const enablingPassAlong =
      passFeesToParent && settings?.passTransactionFeesToCustomers !== true;

    try {
      const result = await toggleTransactionFees({
        passTransactionFeesToCustomers: passFeesToParent,
        cardTransactionFees: cardFeePercentage,
        cardFixedFee: cardFixedFee,
        achTransactionFees: achFeePercentage,
        updateInvoices: false,
        excludedParentIds:
          settings?.excludedParentIds.map((parent) => parent.parentId) ?? [],
      });

      toast.success(
        'Your transaction fee settings have been updated successfully.',
      );

      if (enablingPassAlong) {
        if (result.stripeLinkDisabled) {
          toast.message('Stripe Link disabled', {
            description: PASS_ALONG_LINK_DISABLED_NOTICE,
          });
        } else if (result.bypassStripePaymentMethodConfig === false) {
          toast.message('Stripe Link and pass-along fees', {
            description: PASS_ALONG_LINK_NOT_CHARGED_NOTICE,
          });
        }
      }
    } catch (error) {
      console.error('Error updating transaction fees:', error);
      toast.error(
        'Failed to update transaction fee settings. Please try again.',
      );
    }
  };

  const handleSaveClick = () => {
    // Validate that percentages and fees are positive numbers when fees are enabled
    if (passFeesToParent) {
      const cardPercentage = parseFloat(cardFeePercentage);
      const cardFixed = parseFloat(cardFixedFee);
      const achPercentage = parseFloat(achFeePercentage);

      if (Number.isNaN(cardPercentage) || cardPercentage <= 0) {
        toast.error(
          'Card transaction fee percentage must be a positive number. Please enter a valid percentage.',
        );
        return;
      }

      if (Number.isNaN(cardFixed) || cardFixed <= 0) {
        toast.error(
          'Card fixed fee must be a positive number. Please enter a valid fee amount.',
        );
        return;
      }

      if (isUSD || isAUD) {
        if (Number.isNaN(achPercentage) || achPercentage < 0) {
          toast.error(
            `${isAUD ? 'BECS' : 'ACH'} transaction fee percentage must be a non-negative number. Please enter a valid percentage.`,
          );
          return;
        }
      }
    }

    // Save settings directly
    void handleSave();
  };

  const handlePassFeesChange = (checked: boolean) => {
    if (checked) {
      setShowEnableFeesConfirmation(true);
      return;
    }
    setPassFeesToParent(false);
  };

  const handleConfirmEnablePassFees = () => {
    setPassFeesToParent(true);
    setShowEnableFeesConfirmation(false);
  };

  const linkPassAlongNotice = bypassStripePaymentMethodConfig
    ? PASS_ALONG_LINK_DISABLED_NOTICE
    : PASS_ALONG_LINK_NOT_CHARGED_NOTICE;

  return (
    <div className='space-y-8'>
      <div className='flex items-center gap-6'>
        <Link to='/dashboard' className='flex items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Payment & Billing Settings</h2>
        </Link>
      </div>

      <Separator />

      {isFetchingCurrency || isFetchingSettings ? (
        <div className='flex justify-center py-8'>
          <Loader2 className='size-10 animate-spin' />
        </div>
      ) : (
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
          <Card>
            <CardHeader>
              <CardTitle>Transaction Fee Settings</CardTitle>
              <CardDescription>
                Manage how transaction fees are handled for your active payment
                provider.
                <br />
                <br />
                <strong>
                  Charging a transaction fee may be illegal in some states, so
                  please verify compliance with your local laws before
                  proceeding.
                </strong>
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
              {/* Active Provider Info */}
              <div className='rounded-lg border p-4'>
                <h3 className='mb-2 text-sm font-medium text-muted-foreground'>
                  Active Payment Provider
                </h3>
                {activeProvider ? (
                  <div className='flex items-center gap-2'>
                    <CreditCard className='size-5 text-primary' />
                    <span className='font-medium'>{activeProvider}</span>
                  </div>
                ) : (
                  <p className='text-muted-foreground'>
                    No provider connected.
                  </p>
                )}
              </div>

              {/* Pass Transaction Fees Toggle */}
              <div className='flex flex-col space-y-4'>
                <div className='flex items-start justify-between gap-3'>
                  <div>
                    <div className='flex items-center gap-1.5'>
                      <Label htmlFor='pass-fees' className='text-base'>
                        Pass Transaction Fees to Parent
                      </Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type='button'
                              className='inline-flex shrink-0 rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                              aria-label='Surcharge and processing fee disclosure'
                            >
                              <Info className='size-4' aria-hidden='true' />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent
                            side='top'
                            className='max-w-sm text-left'
                          >
                            <p>{PASS_TRANSACTION_FEES_DISCLOSURE}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <p className='mt-1 text-sm text-muted-foreground'>
                      {passFeesToParent
                        ? 'Parent pays the fee.'
                        : 'You absorb the fee.'}
                    </p>
                  </div>
                  <Switch
                    id='pass-fees'
                    checked={passFeesToParent}
                    onCheckedChange={handlePassFeesChange}
                    disabled={isFetchingSettings}
                  />
                </div>
              </div>

              {/* Fee Configuration (only shown if toggle is ON) */}
              {passFeesToParent && (
                <div className='space-y-6 rounded-lg border p-4'>
                  <h3 className='font-medium'>Transaction Fee Configuration</h3>

                  {/* Card Payment Fees */}
                  <div className='space-y-3'>
                    <div className='flex items-center gap-2'>
                      <CreditCard className='size-4 text-primary' />
                      <Label className='text-sm font-medium'>
                        Card Payment Fees
                      </Label>
                    </div>
                    <div className='space-y-2'>
                      <div className='flex items-center gap-2'>
                        <Input
                          id='card-percentage'
                          type='number'
                          value={cardFeePercentage}
                          onChange={(e) => {
                            setCardFeePercentage(e.target.value);
                          }}
                          className='w-20'
                          step='0.1'
                          min='0'
                          disabled={isFetchingSettings || isUpdating}
                          placeholder={
                            isAUD
                              ? DEFAULT_TRANSACTION_FEES.AUD_CARD_TRANSACTION_PERCENTAGE
                              : DEFAULT_TRANSACTION_FEES.CARD_TRANSACTION_PERCENTAGE
                          }
                        />
                        <span className='text-sm text-muted-foreground'>%</span>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Input
                          id='card-fixed'
                          type='number'
                          value={cardFixedFee}
                          onChange={(e) => {
                            setCardFixedFee(e.target.value);
                          }}
                          className='w-20'
                          step='0.01'
                          min='0'
                          disabled={isFetchingSettings || isUpdating}
                          placeholder='0.30'
                        />
                        <span className='text-sm text-muted-foreground'>
                          Fixed
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ACH Payment Fees */}
                  {(isUSD || isAUD) && (
                    <div className='space-y-3'>
                      <div className='flex items-center gap-2'>
                        <CreditCard className='size-4 text-primary' />
                        <Label className='text-sm font-medium'>
                          {isUSD && 'ACH Payment Fees'}
                          {isAUD && 'BECS Payment Fees'}
                        </Label>
                      </div>
                      <div className='flex items-center gap-2'>
                        <Input
                          id='ach-percentage'
                          type='number'
                          value={achFeePercentage}
                          onChange={(e) => {
                            setAchFeePercentage(e.target.value);
                          }}
                          className='w-20'
                          step='0.1'
                          min='0'
                          disabled={isFetchingSettings || isUpdating}
                          placeholder='0.8'
                        />
                        <span className='text-sm text-muted-foreground'>%</span>
                      </div>
                    </div>
                  )}

                  {/* Fee Information */}
                  <div className='rounded-md bg-muted/50 p-3'>
                    <p className='text-xs text-muted-foreground'>
                      <strong>Note:</strong> These fees will be passed to
                      parents when they make payments. Card fees include both
                      percentage and fixed fee components.
                    </p>
                  </div>

                  <div className='rounded-md border border-amber-200 bg-amber-50 p-3'>
                    <p className='text-xs text-amber-950'>
                      {linkPassAlongNotice}
                    </p>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <Button
                onClick={handleSaveClick}
                className='w-full'
                disabled={isUpdating || isFetchingSettings}
              >
                {isUpdating ? 'Saving...' : 'Save Settings'}
              </Button>
            </CardContent>
          </Card>
          <ExcludedParentsTable
            transactionFeesSettings={settings}
            isFetching={isFetchingSettings}
          />
        </div>
      )}

      <AlertDialog
        open={showEnableFeesConfirmation}
        onOpenChange={setShowEnableFeesConfirmation}
      >
        <AlertDialogContent className='max-w-md'>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Enable passing transaction fees to parents?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className='space-y-3 text-left text-sm leading-relaxed text-muted-foreground'>
                <p>{PASS_TRANSACTION_FEES_DISCLOSURE}</p>
                <p className='font-medium text-foreground'>
                  {linkPassAlongNotice}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className='flex-col-reverse sm:flex-row sm:justify-end'>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmEnablePassFees}>
              I understand
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
